import './index.scss';

import clsx from 'clsx';
import React from 'react';
import { useParams } from 'react-router';

import LongTextByWidth from '@/src/components/common/Elements/LongText/LongTextByWidth';
import { FilterBoxTypes } from '@/src/components/common/FilterBox/V2/FilterBox';
import StatusIndicator from '@/src/components/project/Common/StatusIndicator/StatusIndicator';
import TaskStatusPill from '@/src/components/project/Common/TaskStatusPill';
import TaskName from '@/src/components/template/TaskList/Tasks/Row/TaskName/TaskName';
import { isDelayed, isBefore } from '@/src/helpers/date';
import { SortOrder } from '@/src/util/filters';

import StatusHeader from './Header/StatusHeader';
import TaskNameHeader from './Header/TaskNameHeader';

import ShadowedCard from '../../../../Elements/ShadowedCard/ShadowedCard';
import { UpdateSortOptionsDTO } from '../../../../store/myPortfolio.store';
import { MyPortfolioColumnsEnum } from '../../../../types';
import { Task } from '../../../../types/task';
import Arranger from '../Header/Arranger/Arranger';
import FiltertableHeaderItem from '../Header/HeaderItem/FilterableHeaderItem';
import SortableHeaderItem from '../Header/HeaderItem/SortableHeaderItem';

const DEFAULT_NULL_VALUE = 'None';

export const DEFAULT_SORTING: UpdateSortOptionsDTO = {
  column: MyPortfolioColumnsEnum.DueDate,
  order: SortOrder.ASC,
};

export type ColumnOption = {
  column: { name: MyPortfolioColumnsEnum; type: string };
  name: string;
  title: string;
  default: boolean;
  enabled: boolean;
  dragDisabled: boolean;
  readOnly?: boolean;
  width: string;
  textAlignment: string;
  sorting: boolean | 'chronological' | 'numerical';
  customClasses?: string;
  headerCellClassName?: string;
  HeaderCell?: (props: any) => React.ReactElement;
  Cell?: (props: any) => React.ReactElement;
  filterType?: FilterBoxTypes;
  filterKey?: string;
};

export const ACTION_COLUMNS = [
  {
    id: 'arranger',
    HeaderCell() {
      return <Arranger />;
    },
    customClasses: 'columnManagementAction',
    headerCellClassName: 'tw-sticky tw-right-0',
  },
];

export const COLUMNS: Record<string, ColumnOption> = {
  taskName: {
    column: { name: MyPortfolioColumnsEnum.TaskName, type: 'input' },
    name: 'Task Name',
    title: 'Task Name',
    default: true,
    enabled: true,
    dragDisabled: true,
    readOnly: true,
    width: '500px',
    textAlignment: 'left',
    sorting: false,
    customClasses: 'tw-sticky tw-left-0',
    HeaderCell() {
      return <TaskNameHeader column={COLUMNS[MyPortfolioColumnsEnum.TaskName]} />;
    },
    Cell: ({ entry: task }: { entry: Task }) => {
      return (
        <ShadowedCard className="tw-w-full tw-pl-[15px]">
          <TaskName
            task={{
              id: task.taskId,
              task: task.taskName,
              status: task.taskStatus,
              isMilestone: task.isMilestone,
              isRecurring: task.recurrence,
              projectId: task.projectId,
              ref: React.createRef(),
            }}
          />
        </ShadowedCard>
      );
    },
    // Cell: ({ entry: task }: { entry: Task }) => (
    //   <div className="taskAssignee">
    //     <LongTextByWidth
    //       id={`an_${task.taskId}`}
    //       className={clsx('tw-text-cfo-black')}
    //       maxWidth={150}
    //       value={task.assigneeName}
    //     />
    //   </div>
    // ),
    filterType: FilterBoxTypes.TextContaining,
  },
  taskStatus: {
    column: { name: MyPortfolioColumnsEnum.TaskStatus, type: 'default' },
    name: 'Status',
    title: 'Status',
    default: true,
    enabled: true,
    dragDisabled: false,
    width: '195px',
    textAlignment: 'center',
    sorting: false,
    filterType: FilterBoxTypes.Checkbox,
    HeaderCell() {
      return <StatusHeader column={COLUMNS[MyPortfolioColumnsEnum.TaskStatus]} />;
    },
    Cell: ({ entry: task }: { entry: Task }) => {
      return <TaskStatusPill status={task.taskStatus} />;
    },
    headerCellClassName: 'tw-ml-[15px]',
  },
  dueDate: {
    column: { name: MyPortfolioColumnsEnum.DueDate, type: 'date' },
    name: 'Due Date',
    title: 'Due Date',
    default: true,
    enabled: true,
    dragDisabled: false,
    width: '130px',
    textAlignment: 'center',
    sorting: false,
    filterType: FilterBoxTypes.DateRange,
    HeaderCell() {
      return <FiltertableHeaderItem column={COLUMNS[MyPortfolioColumnsEnum.DueDate]} />;
    },
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className={`taskDueDate ${isDelayed(task.dueDate) ? 'textDue' : ''}`}>
        <span style={{ color: isDelayed(task.dueDate) ? '#D34A46' : 'inherit' }}>
          {task.dueDate}
        </span>
      </div>
    ),
    headerCellClassName: 'tw-ml-[-3px]',
  },
  adjustedDate: {
    column: { name: MyPortfolioColumnsEnum.AdjustedDate, type: 'date' },
    name: 'Adjusted Date',
    title: 'Adjusted Date',
    default: true,
    enabled: true,
    dragDisabled: false,
    width: '150px',
    textAlignment: 'center',
    sorting: false,
    filterType: FilterBoxTypes.DateRange,
    HeaderCell() {
      return <FiltertableHeaderItem column={COLUMNS[MyPortfolioColumnsEnum.AdjustedDate]} />;
    },
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskAdjustedDate">
        <span>{task.adjustedDate || DEFAULT_NULL_VALUE}</span>
      </div>
    ),
  },
  assigneeName: {
    column: { name: MyPortfolioColumnsEnum.AssigneeName, type: 'default' },
    name: 'Assignee',
    title: 'Assignee',
    default: true,
    enabled: true,
    dragDisabled: false,
    width: '150px',
    textAlignment: 'center',
    sorting: false,
    filterKey: MyPortfolioColumnsEnum.AssigneeId,
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskAssignee">
        <LongTextByWidth
          id={`an_${task.taskId}`}
          className={clsx('tw-text-cfo-black')}
          maxWidth={150}
          value={task.assigneeName}
        />
      </div>
    ),
    // headerCellClassName: 'tw-ml-[5px]',
  },
  projectName: {
    column: { name: MyPortfolioColumnsEnum.ProjectName, type: 'default' },
    name: 'Account',
    title: 'Account',
    default: true,
    enabled: true,
    dragDisabled: false,
    width: '150px',
    textAlignment: 'center',
    sorting: false,
    filterKey: MyPortfolioColumnsEnum.ProjectId,
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskAccount">
        <span>{task.projectName}</span>
      </div>
    ),
    // headerCellClassName: 'tw-ml-[5px]',
  },
  workstreamName: {
    column: { name: MyPortfolioColumnsEnum.WorkstreamName, type: 'default' },
    name: 'Workstreams',
    title: 'Workstreams',
    default: true,
    enabled: true,
    dragDisabled: false,
    width: '180px',
    textAlignment: 'center',
    sorting: false,
    filterKey: MyPortfolioColumnsEnum.WorkstreamId,
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskWorkstream">
        <LongTextByWidth
          id={`an_${task.taskId}`}
          className={clsx('tw-text-cfo-black')}
          maxWidth={180}
          value={task.workstreamName}
        />
      </div>
    ),
    // headerCellClassName: 'tw-ml-[5px]',
  },
  approverName: {
    column: { name: MyPortfolioColumnsEnum.ApproverName, type: 'default' },
    name: 'Approver',
    title: 'Approver',
    default: false,
    enabled: false,
    dragDisabled: false,
    width: '150px',
    textAlignment: 'center',
    sorting: false,
    filterKey: MyPortfolioColumnsEnum.ApproverId,
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskApprover">
        <span>{task.approverName || DEFAULT_NULL_VALUE}</span>
      </div>
    ),
    // headerCellClassName: 'tw-ml-[5px]',
  },
  reviewerName: {
    column: { name: MyPortfolioColumnsEnum.ReviewerName, type: 'default' },
    name: 'Reviewer',
    title: 'Reviewer',
    default: false,
    enabled: false,
    dragDisabled: false,
    width: '130px',
    textAlignment: 'center',
    sorting: false,
    filterKey: MyPortfolioColumnsEnum.ReviewerId,
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskReviewer">
        <span>{task.reviewerName || DEFAULT_NULL_VALUE}</span>
      </div>
    ),
    // customClasses: 'taskListHeaderCell',
  },
  startDate: {
    column: { name: MyPortfolioColumnsEnum.StartDate, type: 'date' },
    name: 'Start Date',
    title: 'Start Date',
    default: false,
    enabled: false,
    dragDisabled: false,
    width: '150px',
    textAlignment: 'center',
    sorting: false,
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskStartDate">
        <span>{task.startDate}</span>
      </div>
    ),
    // headerCellClassName: 'tw-ml-[5px]',
  },
  applicableFrom: {
    column: { name: MyPortfolioColumnsEnum.ApplicableFrom, type: 'date' },
    name: 'Applicable from',
    title: 'Applicable from',
    default: false,
    enabled: false,
    dragDisabled: false,
    width: '180px',
    textAlignment: 'center',
    sorting: false,
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskApplicableFrom">
        <span>{task.applicableFrom}</span>
      </div>
    ),
    // headerCellClassName: 'tw-ml-[5px]',
  },
  applicableTo: {
    column: { name: MyPortfolioColumnsEnum.ApplicableTo, type: 'date' },
    name: 'Applicable Until',
    title: 'Applicable Until',
    default: false,
    enabled: false,
    dragDisabled: false,
    width: '165px',
    textAlignment: 'center',
    sorting: false,
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskApplicableTo">
        <span>{task.applicableTo}</span>
      </div>
    ),
    // headerCellClassName: 'tw-ml-[5px]',
  },
  dateCompleted: {
    column: { name: MyPortfolioColumnsEnum.DateCompleted, type: 'date' },
    name: 'Completion Date',
    title: 'Completion Date',
    default: false,
    enabled: false,
    dragDisabled: false,
    width: '180px',
    textAlignment: 'center',
    sorting: false,
    filterType: FilterBoxTypes.DateRange,
    Cell: ({ entry: task }: { entry: Task }) => {
      return (
        <div
          className={`taskCompletionDate ${
            isBefore(task.dueDate, task.dateCompleted) ? 'textDue' : ''
          }`}
        >
          <span
            style={{ color: isBefore(task.dueDate, task.dateCompleted) ? '#D34A46' : 'inherit' }}
          >
            {task.dateCompleted}
          </span>
        </div>
      );
    },
    HeaderCell() {
      return <FiltertableHeaderItem column={COLUMNS[MyPortfolioColumnsEnum.DateCompleted]} />;
    },
    // headerCellClassName: 'tw-ml-[5px]',
  },
  recurrence: {
    column: { name: MyPortfolioColumnsEnum.Recurrence, type: 'default' },
    name: 'Recurrence',
    title: 'Recurrence',
    default: false,
    enabled: false,
    dragDisabled: false,
    width: '150px',
    textAlignment: 'center',
    sorting: false,
    Cell: ({ entry: task }: { entry: Task }) => (
      <div className="taskRecurrence">
        <span>{task.recurrence || DEFAULT_NULL_VALUE}</span>
      </div>
    ),
    // headerCellClassName: 'tw-ml-[5px]',
  },
};

export type MyPortfolioColumns = typeof COLUMNS;

// Not used anymore
export const COMPONENTS = {
  taskStatus: {
    Component: (task) => <StatusIndicator status={task.status} />,
  },
  assigneeName: {
    Component: (task) => (
      <div className="taskAssignee">
        <span>{task.assigneeName}</span>
      </div>
    ),
  },
  dueDate: {
    Component: (task) => (
      <div className={`taskDueDate ${isDelayed(task.dueDate) ? 'textDue' : ''}`}>
        <span style={{ color: isDelayed(task.dueDate) ? '#D34A46' : 'inherit' }}>
          {task.dueDate}
        </span>
      </div>
    ),
  },
  dateCompleted: {
    Component: (task) => {
      return (
        <div
          className={`taskCompletionDate ${
            isBefore(task.dueDate, task.dateCompleted) ? 'textDue' : ''
          }`}
        >
          <span
            style={{ color: isBefore(task.dueDate, task.dateCompleted) ? '#D34A46' : 'inherit' }}
          >
            {task.dateCompleted}
          </span>
        </div>
      );
    },
  },
  projectName: {
    Component: (task) => (
      <div className="taskAccount">
        <span>{task.projectName}</span>
      </div>
    ),
  },
  workstreamName: {
    Component: (task) => (
      <div className="taskWorkstream">
        <span>{task.workstreamName}</span>
      </div>
    ),
  },
  approverName: {
    Component: (task) => (
      <div className="taskApprover">
        <span>{task.approverName || DEFAULT_NULL_VALUE}</span>
      </div>
    ),
  },
  reviewerName: {
    Component: (task) => (
      <div className="taskReviewer">
        <span>{task.reviewerName || DEFAULT_NULL_VALUE}</span>
      </div>
    ),
  },
  startDate: {
    Component: (task) => (
      <div className="taskStartDate">
        <span>{task.startDate}</span>
      </div>
    ),
  },
  applicableFrom: {
    Component: (task) => (
      <div className="taskApplicableFrom">
        <span>{task.applicableFrom}</span>
      </div>
    ),
  },
  applicableTo: {
    Component: (task) => (
      <div className="taskApplicableTo">
        <span>{task.applicableUntil}</span>
      </div>
    ),
  },
  recurrence: {
    Component: (task) => (
      <div className="taskRecurrence">
        <span>{task.recurrence}</span>
      </div>
    ),
  },
};
